import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dog, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, switchMode } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      switchMode('owner');
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        mode: 'owner'
      });

      login(response.data);
      localStorage.setItem('edupaws_mode', 'owner');
      toast.success(`Welcome back, ${response.data.name}!`);
      navigate('/owner/dogs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-edupaws-sage relative overflow-hidden">
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
          <img 
            src="https://edupaws.co/cdn/shop/files/EduPaws_Dog.png?v=1759709026&width=400" 
            alt="Edu Paws Dog" 
            className="w-64 mb-8"
          />
          <h1 className="font-heading text-4xl italic font-bold text-white text-center mb-4">
            Welcome Back to the Pack
          </h1>
          <p className="text-white/80 text-center font-body max-w-md">
            Continue your journey with Edu Paws. Your dog is waiting.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="p-6 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-500 hover:text-edupaws-navy transition-colors font-body"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </button>
          <img 
            src="https://edupaws.co/cdn/shop/files/Edu_Paws_Logo_Updated.png?v=1759285352&width=100" 
            alt="Edu Paws" 
            className="h-10"
          />
        </header>

        {/* Form */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <h2 className="font-heading text-3xl italic font-bold text-edupaws-navy mb-2">
              Welcome Back!
            </h2>
            <p className="text-gray-500 font-body mb-8">
              Sign in to continue your journey.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-edupaws-navy font-body font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="mt-1.5 h-12 bg-gray-50 border-gray-200 rounded-xl font-body focus:border-edupaws-sage focus:ring-edupaws-sage"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-edupaws-navy font-body font-medium">
                  Password
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="h-12 bg-gray-50 border-gray-200 rounded-xl pr-12 font-body focus:border-edupaws-sage focus:ring-edupaws-sage"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full bg-edupaws-navy text-white font-semibold py-3 px-6 rounded-full
                           shadow-[0_4px_0_#2D3B47] hover:shadow-[0_2px_0_#2D3B47] hover:translate-y-[2px] 
                           transition-all font-body disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            {/* Forgot Password */}
            <div className="mt-4 text-center">
              <Link to="/forgot-password" className="text-sm text-gray-500 hover:text-edupaws-sage font-body">
                Forgot your password?
              </Link>
            </div>

            {/* Sign Up Link */}
            <p className="mt-8 text-center text-gray-500 font-body">
              Don't have an account?{' '}
              <Link to="/signup" className="text-edupaws-sage font-medium hover:underline">
                Sign Up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
